import React from 'react';
import { Typography, Badge } from 'antd';
import { ShoppingOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../../types/store';
import { setCartDrawerOpen, selectCartTotalElements } from '../slice/storefrontSlice';

const { Text } = Typography;

export const StorefrontHeader: React.FC = () => {
  const dispatch = useDispatch();

  const config = useSelector((state: RootState) => state.storefront.config);

  const rawLogo = config?.settings?.logoUrl ?? config?.partner?.logoUrl ?? null;
  // SVG escala solo; la transformación de ImageKit (alto 80px, 2x del tile) es para raster
  const logoSrc = rawLogo && !/\.svg($|\?)/i.test(rawLogo) ? `${rawLogo}?tr=h-80` : rawLogo;

  const cartItemCount = useSelector(selectCartTotalElements);

  return (
    <header className="storefront-header">
      <div className="sf-header-inner">
        {/* Left: Brand (una sola linea: logo + nombre) */}
        <div className="sf-header-brand">
          <div
            className="sf-header-logo"
            style={{
              backgroundColor: logoSrc ? 'var(--color-card)' : 'var(--color-accent)',
              color: 'var(--color-accent-contrast)',
            }}
          >
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={config?.partner?.name}
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : config?.partner?.name ? (
              config.partner.name.charAt(0).toUpperCase()
            ) : (
              'S'
            )}
          </div>

          <Text strong className="header-title">
            {config?.partner?.name || 'SAMA'}
          </Text>
        </div>

        {/* Center: Banner - desktop only */}
        {config?.partner?.bannerUrl && (
          <div className="header-banner">
            <img
              src={config.partner.bannerUrl}
              alt={config.partner.name}
              style={{ maxHeight: 28, objectFit: 'contain', maxWidth: '100%' }}
            />
          </div>
        )}

        {/* Right: Solo Carrito con badge */}
        <div className="sf-header-actions">
          <Badge
            count={cartItemCount}
            offset={[-4, 4]}
            color="var(--color-badge)"
            className="header-cart-badge"
          >
            <button
              onClick={() => dispatch(setCartDrawerOpen(true))}
              aria-label={`Pedido, ${cartItemCount} articulos`}
              className="header-cart-btn"
            >
              <ShoppingOutlined style={{ fontSize: 19 }} />
            </button>
          </Badge>
        </div>
      </div>

      <style>{`
        .storefront-header {
          position: sticky;
          top: 0;
          z-index: 200;
          height: 64px;
          padding: 0 14px;
          display: flex;
          align-items: center;
          background-color: rgba(var(--color-paper-rgb), 0.9);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid var(--color-border);
        }
        @media (min-width: 768px) {
          .storefront-header { height: 68px; padding: 0 20px; }
        }

        .sf-header-inner {
          max-width: 1280px;
          width: 100%;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          min-width: 0;
        }
        .sf-header-brand {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
          flex: 1 1 auto;
        }
        .sf-header-logo {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          font-weight: 800;
          font-family: var(--font-display);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          flex-shrink: 0;
          letter-spacing: -0.03em;
          overflow: hidden;
          border: 1px solid var(--color-border);
        }
        .header-title {
          font-size: 15px !important;
          font-family: var(--font-display);
          color: var(--color-ink) !important;
          font-weight: 700;
          letter-spacing: -0.03em;
          line-height: 1.1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 0;
        }

        .header-banner {
          display: none;
          flex: 0 1 360px;
          justify-content: center;
          padding: 0 16px;
          min-width: 0;
        }
        @media (min-width: 1024px) {
          .header-banner { display: flex; }
          .storefront-header > div { gap: 24px; }
        }

        .sf-header-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }

        /* Pastilla de tasa del dia */
        .header-rate {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          height: 32px;
          padding: 0 11px;
          border-radius: var(--radius-full);
          background-color: var(--color-gold-soft);
          color: var(--color-ink);
          font-family: var(--font-mono);
          font-size: 11.5px;
          font-weight: 600;
          letter-spacing: -0.02em;
          white-space: nowrap;
        }
        .header-rate-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: var(--color-gold);
          flex-shrink: 0;
        }
        .rate-short { display: none; }
        @media (max-width: 520px) {
          .rate-full { display: none; }
          .rate-short { display: inline; }
        }

        /* Mis pedidos: icono en movil */
        .header-orders-btn {
          background-color: var(--color-card);
          color: var(--color-ink);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-full);
          padding: 0 14px;
          height: 40px;
          font-weight: 600;
          font-size: 13.5px;
          font-family: var(--font-body);
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 7px;
          white-space: nowrap;
          transition: background-color var(--duration-fast) var(--ease-out),
            border-color var(--duration-fast) var(--ease-out);
        }
        @media (max-width: 640px) {
          .header-orders-btn {
            width: 40px;
            padding: 0;
            justify-content: center;
          }
          .orders-label { display: none; }
        }
        .header-orders-btn:hover {
          border-color: var(--color-muted-soft);
          background-color: var(--color-surface);
        }

        /* Carrito: icono circular de 40px con badge rojo */
        .header-cart-btn {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: var(--color-card);
          color: var(--color-ink);
          border: 1px solid var(--color-border);
          border-radius: 50%;
          cursor: pointer;
          box-shadow: var(--shadow-1);
          transition: transform var(--duration-fast) var(--ease-out),
            border-color var(--duration-fast) var(--ease-out),
            background-color var(--duration-fast) var(--ease-out);
        }
        .header-cart-btn:hover {
          border-color: var(--color-accent);
          color: var(--color-accent);
        }
        .header-cart-btn:active { transform: scale(0.94); }
        .header-cart-badge .ant-badge-count {
          font-family: var(--font-mono);
          font-size: 11px;
          font-weight: 700;
          min-width: 18px;
          height: 18px;
          line-height: 18px;
          color: var(--color-badge-contrast);
          box-shadow: 0 0 0 2px var(--color-paper);
        }

        @media (prefers-reduced-motion: reduce) {
          .header-cart-btn, .header-orders-btn { transition: none; }
        }
      `}</style>
    </header>
  );
};
